/**
 * Minimal typed GraphQL client for the swapgate worker.
 *
 * Why no Apollo/urql: the playground only ever issues a handful of
 * named operations with no normalized cache, optimistic UI, or fragment
 * composition. A 30-line fetch wrapper covers it without adding ~50 KB
 * of bundled state-management machinery.
 *
 * Transport: Apollo Persisted Queries (APQ). The worker's persisted-ops
 * plugin enforces `extensions.persistedQuery.sha256Hash` outside dev and
 * REJECTS bodies that include both the hash and a raw query
 * (`CANNOT_SEND_PQ_ID_AND_BODY`). The SPA computes sha256(query) at
 * call time via WebCrypto and sends ONLY the hash + variables — bytes
 * are guaranteed to match the publisher's because both read the same
 * `apps/playground/queries/*.graphql` files.
 *
 * Auth: the `X-Swapgate-Key` header carries the API key the worker
 * looks up by sha256. Set it via the EndpointBar; never logged.
 */

import { sha256Hex } from "./sha256.js";

export interface GraphQLRequest {
  readonly endpoint: string;
  readonly apiKey: string;
  readonly query: string;
  readonly operationName?: string;
  readonly variables?: Record<string, unknown>;
}

/**
 * GraphQL error envelope as returned by Yoga. The playground only renders
 * `message`; `extensions` is preserved for the raw-JSON view.
 */
export interface GraphQLErrorPayload {
  readonly message: string;
  readonly extensions?: Record<string, unknown>;
  readonly path?: readonly (string | number)[];
}

export interface GraphQLResponse<TData> {
  readonly data?: TData;
  readonly errors?: readonly GraphQLErrorPayload[];
}

/**
 * What `gqlFetch` exposes to the caller. The `requestBody` is the
 * exact JSON envelope sent to the worker — useful for the playground's
 * "Raw request" expander so a developer can reproduce the call via curl.
 * Returning it from the function instead of constructing it twice in
 * the App keeps the SPA's display in lock-step with the wire format.
 */
export interface GraphQLCallResult<TData> {
  readonly response: GraphQLResponse<TData>;
  readonly requestBody: Record<string, unknown>;
}

/**
 * Sends a POST to `${endpoint}/graphql` with the canonical Yoga body.
 * Resolves with `{ data, errors }` whatever the HTTP status is so the
 * caller can render `errors` in the UI (HTTP 200 with `errors` is the
 * idiomatic GraphQL shape).
 *
 * Throws only on network failures or non-JSON responses — those are
 * configuration bugs (wrong endpoint, CORS denied, etc.) and surface
 * as a banner in the EndpointBar.
 */
export async function gqlFetch<TData>(
  req: GraphQLRequest,
): Promise<GraphQLCallResult<TData>> {
  const url = `${req.endpoint.replace(/\/$/, "")}/graphql`;
  const sha256Hash = await sha256Hex(req.query);

  const requestBody: Record<string, unknown> = {
    // APQ: send the hash only — the worker resolves it to the stored
    // query string in KV (`pq:<hash>`). Sending `query` alongside the
    // hash trips the worker's `CANNOT_SEND_PQ_ID_AND_BODY` guard.
    extensions: { persistedQuery: { version: 1, sha256Hash } },
    ...(req.operationName !== undefined
      ? { operationName: req.operationName }
      : {}),
    ...(req.variables !== undefined ? { variables: req.variables } : {}),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-swapgate-key": req.apiKey,
    },
    body: JSON.stringify(requestBody),
  });

  const contentType = res.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    throw new Error(
      `Unexpected response from ${url}: HTTP ${res.status} ${res.statusText} (content-type=${contentType || "<none>"})`,
    );
  }
  const response = (await res.json()) as GraphQLResponse<TData>;
  return { response, requestBody };
}

/**
 * The four queries the playground sends. Sourced from `apps/playground/queries/*.graphql`
 * via Vite's `?raw` import — single file is the source of truth so the
 * `scripts/publish-queries.ts` publisher and the SPA hash THE EXACT SAME bytes
 * (whitespace included). Copy-paste drift between client and persisted-ops
 * KV would surface as `PersistedQueryNotFound` from Yoga.
 */
import buildTxQuery from "../../queries/buildTx.graphql?raw";
import chainsQuery from "../../queries/chains.graphql?raw";
import tokensQuery from "../../queries/tokens.graphql?raw";
import txStatusQuery from "../../queries/txStatus.graphql?raw";

export const BUILD_TX_QUERY: string = buildTxQuery;
export const CHAINS_QUERY: string = chainsQuery;
export const TOKENS_QUERY: string = tokensQuery;
export const TX_STATUS_QUERY: string = txStatusQuery;
